import React, { useRef } from 'react';
import { User, Calendar, School, Clock, Mail, Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";

interface StudentProfileModalProps {
  student: any | null;
  isOpen: boolean;
  onClose: () => void;
}

export function StudentProfileModal({ student, isOpen, onClose }: StudentProfileModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string | null) => {
    if (!dateString || 
        dateString === '' || 
        dateString.includes('0001-01-01') || 
        dateString.includes('BC')) {
      return 'Nunca acessou';
    }
    
    try {
      // Se a data já estiver formatada com /, retorna como está
      if (dateString.includes('/')) {
        return dateString;
      }

      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('Data inválida:', dateString);
        return 'Data inválida';
      }

      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Erro ao formatar data:', error, dateString);
      return 'Data inválida';
    }
  };

  const handleDownload = async () => {
    if (!contentRef.current) return;

    try {
      const element = contentRef.current;
      element.classList.add('downloading');

      const canvas = await html2canvas(element, {
        backgroundColor: '#1C1D2C',
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true
      });

      element.classList.remove('downloading');

      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `ficha-${student?.nome.toLowerCase().replace(/\s+/g, '-')}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Erro ao gerar imagem:', err);
      alert('Erro ao gerar a imagem. Por favor, tente novamente.');
    }
  };

  if (!student) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="p-6 border-b">
          <DialogTitle>Ficha do Aluno</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-4 border-b">
          <Button onClick={handleDownload}>
            <Download className="w-4 h-4 mr-2" />
            Baixar Ficha
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-6">
            <div ref={contentRef} className="space-y-6">
              {/* Informações Pessoais */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Informações Pessoais
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-muted-foreground">Nome:</span>
                      <p className="mt-1">{student.nome}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">E-mail:</span>
                      <p className="mt-1">{student.email}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">RA:</span>
                      <p className="mt-1">{student.ra || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">CPF:</span>
                      <p className="mt-1">{student.documento}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Telefone:</span>
                      <p className="mt-1">{student.telefone || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Sexo:</span>
                      <p className="mt-1">{student.sexo || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Data de Nascimento:</span>
                      <p className="mt-1">{student.data_nascimento ? formatDate(student.data_nascimento) : '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Tipo:</span>
                      <p className="mt-1">{student.tipo || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cursos */}
              {student.cursos && student.cursos.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <School className="w-5 h-5" />
                      Cursos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {student.cursos.map((curso, index) => (
                        <Card key={index}>
                          <CardHeader>
                            <CardTitle className="text-base">{curso.nome_curso}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm text-muted-foreground">Entrega</p>
                                <p className="mt-1">{curso.entrega || '-'}</p>
                              </div>

                              <div>
                                <p className="text-sm text-muted-foreground">Progresso</p>
                                <div className="flex items-center gap-4 mt-2">
                                  <Progress value={curso.progresso || 0} className="flex-1" />
                                  <span className="text-sm min-w-[3rem] text-right">
                                    {curso.progresso || 0}%
                                  </span>
                                </div>
                              </div>

                              <div>
                                <p className="text-sm text-muted-foreground">Data de Início</p>
                                <p className="mt-1">{formatDate(curso.data_inicio)}</p>
                              </div>
                              <div>
                                <p className="text-sm text-muted-foreground">Previsão de Conclusão</p>
                                <p className="mt-1">{formatDate(curso.data_conclusao_prevista)}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Último Acesso */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Último Acesso
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <p>{formatDate(student.ultimo_acesso)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}